import { useState, useEffect, useRef, useMemo } from "react";
import classnames from "classnames";
import { useTable, useSortBy, SortingRule, Column } from "react-table";
import { ChampionLink } from "../../common/ChampionLink";
import { TableScrollHandler, useLazyLoadTableRows, TIER_LIST_PAGE_SIZE } from "../../common/TableScrollHandler";

import { useResponsive } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { TooltipContainer } from "@outplayed/tooltips";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { ArenaDuoInfo } from "@ugg/shared/api/data-parser/tier-lists/arena-duo-tier-list";
import { getTierColor, getLuTierColor, getLuTier } from "@ugg/shared/utils/tier-helpers";
import { round } from "@ugg/shared/utils/math";
import { ArenaModeType } from "@ugg/shared/utils/queue-type-helpers";

interface ArenaTierListTableProps {
  data: ArenaDuoInfo[];
  arenaModeType: ArenaModeType;
}

export function ArenaDuoTierListTable(props: ArenaTierListTableProps) {
  const { getChampionName } = getRiotAssetsContext();
  const prevSort = useRef<SortingRule<ArenaDuoInfo>[] | null>(null);
  const [pageSize, setPageSize] = useState(TIER_LIST_PAGE_SIZE);
  const { currentBreakpoint } = useResponsive();

  const { data, arenaModeType } = props;

  const tableData = useMemo(() => {
    return data || [];
  }, [data]);

  const isMobile = ["MOBILE_SMALL", "MOBILE_MEDIUM", "MOBILE_LARGE"].includes(currentBreakpoint);
  const isMobileSmall = ["MOBILE_SMALL", "MOBILE_MEDIUM"].includes(currentBreakpoint);

  const hiddenColumns = [
    isMobileSmall && "duoPickRate",
    isMobile && "duoBanRate",
    isMobileSmall && "duoAvgPlacement",
    isMobileSmall && "duoTop1Rate",
  ]
    .filter((col) => !!col)
    .map((col) => String(col));

  const columns = useMemo<Column<ArenaDuoInfo>[]>(
    () => [
      {
        id: "rank",
        accessor: "duo1",
        Header: "Rank",
        responsiveClassName: "xxxs:w-[40px] xs:w-[50px] xxxs:min-w-[40px] xs:min-w-[40px] xxxs:max-w-[40px] xs:max-w-[50px]",
        headerClassName: "rank xxxs:max-xs:invisible",
        className: "rank",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
      },
      {
        Header: <div className="flex justify-center flex-1">Champion</div>,
        accessor: "duo1",
        responsiveClassName: `
          xxxs:min-w-[80px] xs:min-w-[220px]
          max-[800px]:!min-w-[80px] max-[800px]:!w-[80px] 
          max-[800px]:!flex-[80] max-[800px]:!justify-center
          min-[800px]:!justify-start
        `,
        headerClassName: "champion",
        className: "champion",
        sortInverted: true,
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.champion_id);
          const champNameB = getChampionName(b.values.champion_id);
          return champNameB.localeCompare(champNameA);
        },
        Cell: (props) => (
          <div className="flex xs:flex-1 min-[800px]:w-full max-[800px]:gap-[8px] max-[800px]:justify-center">
            <ChampionLink
              className="max-[800px]:[&_.champion-name]:hidden max-[800px]:[&>div]:!p-0 overflow-hidden"
              championId={props.row.original.duo1}
              dim={30}
            />
            <ChampionLink
              className="max-[800px]:[&_.champion-name]:hidden max-[800px]:[&>div]:!p-0 overflow-hidden"
              championId={props.row.original.duo2}
              dim={30}
            />
          </div>
        ),
      },
      {
        Header: (props) => (
          <TooltipContainer
            tooltipInfo={
              <span>
                The U.GG tier list is derived from standard deviations on
                <br />
                champion winrates, pick rates and ban rates to identify the
                <br />
                strongest champions in the current meta.
              </span>
            }
          >
            <div>Tier</div>
          </TooltipContainer>
        ),
        accessor: "stdevs",
        responsiveClassName: "xxxs:min-w-[50px] xxxs:max-w-[70px]",
        headerClassName: "tier",
        className: "tier",
        sortDescFirst: true,
        sortType: (a, b) => {
          return b.values.stdevs - a.values.stdevs;
        },
        Cell: ({ value }) => (
          <span className={getLuTierColor(Number(value.toFixed(2)))}>
            <b>{getLuTier(Number(value.toFixed(2)))}</b>
          </span>
        ),
      },
      {
        Header: "Avg. Place",
        accessor: "duoAvgPlacement",
        responsiveClassName: "xxxs:min-w-[70px] xxxs:max-w-[90px]",
        headerClassName: "avg-placement",
        className: "avg-placement",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.duoAvgPlacement - b.values.duoAvgPlacement;
        },
        Cell: ({ value }) => <span>{round(value, 2).toFixed(2)}</span>,
      },
      {
        Header: "1st Rate",
        accessor: "duoTop1Rate",
        responsiveClassName: "xxxs:min-w-[80px] xxxs:max-w-[90px]",
        headerClassName: "first_rate",
        className: "first_rate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.duoTop1Rate - b.values.duoTop1Rate;
        },
        Cell: ({ value }) => <span>{value.toFixed(1) + "%"}</span>,
      },
      {
        Header: arenaModeType === 16 ? "Top 4 Rate" : "Top 2 Rate",
        accessor: "duoTop2Rate",
        responsiveClassName: "xxxs:min-w-[80px] xxxs:max-w-[90px]",
        headerClassName: "top2_rate",
        className: "top2_rate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.duoTop2Rate - b.values.duoTop2Rate;
        },
        Cell: ({ value }) => (
          <span className={getTierColor(round(value, 1))}>
            <b>{round(value, 1).toFixed(1) + "%"}</b>
          </span>
        ),
      },
      {
        Header: "Duo Pick",
        accessor: "duoPickRate",
        responsiveClassName: "xxxs:min-w-[80px] xxxs:max-w-[90px]",
        headerClassName: "pickrate",
        className: "pickrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.duoPickRate - b.values.duoPickRate;
        },
        Cell: ({ value }) => <span>{value.toFixed(1) + "%"}</span>,
      },
      {
        Header: "Duo Ban",
        accessor: "duoBanRate",
        responsiveClassName: "xxxs:min-w-[80px] xxxs:max-w-[90px]",
        headerClassName: "banrate",
        className: "banrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.duoBanRate - b.values.duoBanRate;
        },
        Cell: ({ value }) => <span>{value.toFixed(1) + "%"}</span>,
      },
      {
        Header: "Matches",
        accessor: "duoCombinedMatches",
        responsiveClassName: "xxxs:min-w-[80px] xxxs:max-w-[90px]",
        headerClassName: "matches",
        className: "matches",
        sortDescFirst: true,
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
    ],
    [arenaModeType],
  );

  const columnStyle = () => ({
    flex: `1 1 100%`,
  });

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setHiddenColumns } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageIndex: 0,
        sortBy: prevSort.current || [{ id: "duoTop2Rate", desc: true }],
        hiddenColumns,
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  useEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [JSON.stringify(hiddenColumns)]);

  useEffect(() => {
    setPageSize(TIER_LIST_PAGE_SIZE);
  }, [tableData]);

  const tableRef = useRef<HTMLDivElement>(null);
  useLazyLoadTableRows({
    tableData,
    tableRef,
    pageSize,
    setPageSize,
  });

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(TIER_LIST_PAGE_SIZE);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  return (
    <div className="tier-list-container">
      <div
        ref={tableRef}
        {...getTableProps({
          className: "content-section ReactTable ugg-table-2 tier-list arena-duo-tier-list",
        })}
      >
        <div className={`rt-thead -header`}>
          {headerGroups.map((headerGroup, index) => (
            <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
              {headerGroup.headers.map((column, index) => {
                const thClassNames = classnames([
                  "rt-th",
                  column.headerClassName,
                  column.responsiveClassName,
                  column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                  column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                ]);

                return (
                  <div
                    className={thClassNames}
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({
                        style: columnStyle(),
                      }),
                    )}
                  >
                    {column.render("Header")}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps({ className: "rt-tbody" })}>
          {rows.map((row, i) => {
            if (i >= pageSize) {
              return null;
            }
            prepareRow(row);
            return (
              <div key={i} className="rt-tr-group">
                <div {...row.getRowProps({ className: "rt-tr" })}>
                  {row.cells.map((cell, index) => {
                    const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
                      sorted: cell.column.isSorted,
                      "is-in-odd-row": i % 2 === 0,
                    });

                    return (
                      <div
                        {...cell.getCellProps({
                          className: tdClassNames,
                          style: columnStyle(),
                        })}
                      >
                        {cell.render("Cell", { index: i })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <TableScrollHandler />
    </div>
  );
}
